import {ReportingSubscriptionRecord} from "../types/Responses/ReportingSubscriptionsResponse";

export type FilterGroupKey =
    'brokers'
    | 'users'
    | 'payment_methods'
    | 'providers'
    | 'statuses'
    | 'countries'
    | 'currencies';

export interface FilterTagGroup {
    key: FilterGroupKey;
    tags: string[];
}

export interface SimpleEntity {
    id: number;
    name: string;
}

export interface CurrencyLookup {
    id: number;
    code?: string;
    name?: string;
}

export interface ReportingFilterLookups {
    brokers?: SimpleEntity[];
    users?: SimpleEntity[];
    paymentMethods?: SimpleEntity[];
    providers?: SimpleEntity[];
    statuses?: SimpleEntity[];
    countries?: SimpleEntity[];
    currencies?: CurrencyLookup[];
}

export const normalizeReportingFilters = (
    filters: ReportingSubscriptionRecord['filters']
): Record<string, unknown> | null => {
    if (!filters) {
        return null;
    }
    if (typeof filters === 'string') {
        try {
            const parsed = JSON.parse(filters);
            if (parsed && typeof parsed === 'object') {
                return parsed as Record<string, unknown>;
            }
            return null;
        } catch {
            return null;
        }
    }
    return filters;
};

const parseIds = (value: unknown): number[] => {
    if (!value && value !== 0) {
        return [];
    }
    if (Array.isArray(value)) {
        return value
            .map((item) => typeof item === 'string' ? parseInt(item, 10) : Number(item))
            .filter((id) => !Number.isNaN(id));
    }
    if (typeof value === 'number') {
        return Number.isNaN(value) ? [] : [value];
    }
    if (typeof value === 'string') {
        return value
            .split(',')
            .map((item) => parseInt(item, 10))
            .filter((id) => !Number.isNaN(id));
    }
    return [];
};

const mapIdsToNames = (ids: number[], collection?: LookupEntity[], fallbackPrefix: string = '#'): string[] => {
    if (!collection || !ids.length) {
        return ids.map((id) => `${fallbackPrefix}${id}`);
    }

    return ids.map((id) => {
        const match = collection.find((item) => item.id === id);
        if (!match) {
            return `${fallbackPrefix}${id}`;
        }
        if ('code' in match && match.code) {
            return match.code;
        }
        return match.name ?? `${fallbackPrefix}${id}`;
    });
};

type LookupEntity = SimpleEntity | CurrencyLookup;

export const buildReportingFilterGroups = (
    filters: ReportingSubscriptionRecord['filters'],
    lookups: ReportingFilterLookups
): FilterTagGroup[] => {
    const normalizedFilters = normalizeReportingFilters(filters);
    if (!normalizedFilters) {
        return [];
    }

    const groups: FilterTagGroup[] = [];

    const brokerTags = mapIdsToNames(parseIds(normalizedFilters?.['broker']), lookups.brokers, 'Broker #');
    if (brokerTags.length) {
        groups.push({key: 'brokers', tags: brokerTags});
    }

    const userTags = mapIdsToNames(parseIds(normalizedFilters?.['user']), lookups.users, 'User #');
    if (userTags.length) {
        groups.push({key: 'users', tags: userTags});
    }

    const paymentMethodTags = mapIdsToNames(parseIds(normalizedFilters?.['payment_method']), lookups.paymentMethods, 'Method #');
    if (paymentMethodTags.length) {
        groups.push({key: 'payment_methods', tags: paymentMethodTags});
    }

    const providerTags = mapIdsToNames(parseIds(normalizedFilters?.['provider']), lookups.providers, 'Provider #');
    if (providerTags.length) {
        groups.push({key: 'providers', tags: providerTags});
    }

    const statusTags = mapIdsToNames(parseIds(normalizedFilters?.['status']), lookups.statuses, 'Status #');
    if (statusTags.length) {
        groups.push({key: 'statuses', tags: statusTags});
    }

    const countryTags = mapIdsToNames(parseIds(normalizedFilters?.['country']), lookups.countries, 'Country #');
    if (countryTags.length) {
        groups.push({key: 'countries', tags: countryTags});
    }

    const currencyTags = mapIdsToNames(parseIds(normalizedFilters?.['currency']), lookups.currencies, 'Currency #');
    if (currencyTags.length) {
        groups.push({key: 'currencies', tags: currencyTags});
    }

    return groups;
};
